import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Cropper, { Area, Point } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

interface ImageCropEditorProps {
  imageUrl: string;
  onConfirm: (croppedAreaPixels: Area) => void;
  onCancel: () => void;
}

const ImageCropEditor: React.FC<ImageCropEditorProps> = ({ imageUrl, onConfirm, onCancel }) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleCropComplete = useCallback((_croppedArea: Area, pixelArea: Area) => {
    setCroppedAreaPixels(pixelArea);
  }, []);

  const handleConfirm = () => {
    if (croppedAreaPixels) {
      onConfirm(croppedAreaPixels);
    }
  };

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.75)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 520,
          borderRadius: 8,
          backgroundColor: '#fff',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Crop Photo</h3>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7280' }}>Drag to reposition · Slider to zoom</p>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: '#9ca3af',
              fontSize: 20,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Crop area */}
        <div style={{ position: 'relative', width: '100%', height: 360, backgroundColor: '#111827' }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>

        {/* Footer — zoom slider + confirm */}
        <div style={{ padding: '16px 24px 20px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: '#374151', minWidth: 36 }}>Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number.parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: '#6b7280', minWidth: 32, textAlign: 'right' }}>{zoom.toFixed(1)}×</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              onClick={onCancel}
              style={{
                padding: '8px 18px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: '#fff',
                fontSize: 14,
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!croppedAreaPixels}
              style={{
                padding: '8px 22px',
                borderRadius: 6,
                border: 'none',
                background: '#2563eb',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                cursor: croppedAreaPixels ? 'pointer' : 'not-allowed',
                opacity: croppedAreaPixels ? 1 : 0.5,
              }}
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default ImageCropEditor;
